import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { MediaItem } from "@/types/media";
import { formatViews } from "@/lib/media";

interface HeroCarouselProps {
  items: MediaItem[];
}

export default function HeroCarousel({ items }: HeroCarouselProps) {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused || items.length <= 1) return;
    const timer = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % items.length);
    }, 6000);
    return () => window.clearInterval(timer);
  }, [paused, items.length]);

  if (items.length === 0) return null;

  const go = (next: number) => setIndex((next + items.length) % items.length);
  const current = items[index];

  return (
    <section
      className="relative h-[560px] w-full overflow-hidden bg-background-950 md:h-[660px]"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      aria-label="编辑推荐影视轮播"
    >
      {items.map((item, i) => (
        <div
          key={item.id}
          className={`absolute inset-0 transition-opacity duration-1000 ${i === index ? "opacity-100" : "opacity-0"}`}
          aria-hidden={i !== index}
        >
          <img
            src={item.backdrop}
            alt={`《${item.title}》主视觉`}
            className={`h-full w-full object-cover object-top ${i === index ? "animate-slow-zoom" : ""}`}
          />
        </div>
      ))}

      <div className="absolute inset-0 bg-gradient-to-t from-background-950 via-background-950/75 to-background-950/30"></div>
      <div className="absolute inset-0 bg-gradient-to-r from-background-950/95 via-background-950/45 to-transparent"></div>

      <div className="relative mx-auto flex h-full w-full max-w-7xl flex-col justify-end px-4 pb-16 md:px-8 md:pb-20">
        <div key={current.id} className="max-w-2xl animate-fade-up">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-500/15 px-3 py-1 text-xs font-medium text-accent-400 md:text-sm">
            <i className="ri-fire-fill"></i>
            编辑推荐
          </span>
          <h2 className="mt-3 font-heading text-3xl font-bold leading-tight text-foreground-50 md:text-5xl">
            {current.title}
          </h2>
          <p className="mt-2 text-sm text-accent-300 md:text-base">{current.tagline}</p>

          <div className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs text-foreground-200 md:text-sm">
            <span className="flex items-center gap-1 text-accent-400">
              <i className="ri-star-fill"></i>
              {current.rating.toFixed(1)}
            </span>
            <span>{current.year}</span>
            <span className="text-foreground-600">·</span>
            <span>{current.region}</span>
            <span className="text-foreground-600">·</span>
            <span>{current.genres.join(" / ")}</span>
            <span className="text-foreground-600">·</span>
            <span>{formatViews(current.views)} 热度</span>
          </div>

          <p className="mt-4 max-w-xl text-sm leading-relaxed text-foreground-300 md:text-base">
            {current.synopsis.length > 96 ? `${current.synopsis.slice(0, 96)}…` : current.synopsis}
          </p>

          <div className="mt-6 flex flex-wrap items-center gap-3">
            <Link
              to={`/detail/${current.id}`}
              className="flex items-center gap-2 rounded-md bg-accent-500 px-5 py-2.5 text-sm font-semibold text-background-950 whitespace-nowrap transition-colors hover:bg-accent-400"
            >
              <i className="ri-play-circle-line text-lg"></i>
              查看详情
            </Link>
            <Link
              to={current.type === "tv" ? "/tv" : "/movie"}
              className="flex items-center gap-2 rounded-md border border-background-600 bg-background-900/60 px-5 py-2.5 text-sm font-medium text-foreground-100 whitespace-nowrap backdrop-blur transition-colors hover:border-accent-400 hover:text-accent-400"
            >
              <i className="ri-film-line"></i>
              进入{current.type === "tv" ? "电视剧" : "电影"}频道
            </Link>
          </div>
        </div>

        <div className="mt-8 flex items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            {items.map((item, i) => (
              <button
                key={item.id}
                type="button"
                onClick={() => setIndex(i)}
                aria-label={`切换到《${item.title}》`}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  i === index ? "w-8 bg-accent-500" : "w-4 bg-background-500 hover:bg-background-400"
                }`}
              ></button>
            ))}
          </div>

          <div className="hidden items-center gap-2 md:flex">
            <button
              type="button"
              onClick={() => go(index - 1)}
              aria-label="上一部"
              className="flex h-10 w-10 items-center justify-center rounded-full border border-background-600 bg-background-900/60 text-foreground-100 backdrop-blur transition-colors hover:border-accent-400 hover:text-accent-400"
            >
              <i className="ri-arrow-left-s-line text-xl"></i>
            </button>
            <button
              type="button"
              onClick={() => go(index + 1)}
              aria-label="下一部"
              className="flex h-10 w-10 items-center justify-center rounded-full border border-background-600 bg-background-900/60 text-foreground-100 backdrop-blur transition-colors hover:border-accent-400 hover:text-accent-400"
            >
              <i className="ri-arrow-right-s-line text-xl"></i>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}